import { useState } from 'react';

import { Button, cn, Select } from '@src/components/ui';
import { useQuery, useSaveItemSpecificsMutation } from '@src/query';

import { Skeleton, WidgetError } from '../Dashboard/WidgetState';
import { Toaster, useToasts } from './useToasts';


/** Inner: owns the local edit state. Re-keyed by a stable signature of the loaded
 * values (see the outer) so a refetch re-seeds edits by remount rather than a
 * setState-in-effect (react-hooks/set-state-in-effect) — mirrors CopyModalInner's
 * `key={part.id}` trick. Renders its own `<Toaster>`: `useToasts` is a local
 * queue, so the page-level Toaster (a separate instance) can't surface our push. */
function ItemSpecificsChecklistInner(props) {
  const { sellerId, partId, canWrite, onSaved, summary, items } = props;
  const { toasts, push } = useToasts();
  const save = useSaveItemSpecificsMutation();

  const [values, setValues] = useState(() => {
    const seed = {};
    items.forEach((item) => {
      seed[item.code] = item.value ?? '';
    });
    return seed;
  });

  const setValue = (code, value) => setValues((previous) => ({ ...previous, [code]: value }));

  const onSave = () => {
    // Send every edited code — including blanks — so clearing a field removes it
    // server-side (the PATCH replaces the stored specifics map).
    const specifics = {};
    items.forEach((item) => {
      specifics[item.code] = values[item.code] ?? '';
    });
    save.mutate(
      { sellerId, partId, specifics },
      {
        onSuccess: () => {
          push('Item specifics saved');
          onSaved?.();
        },
      },
    );
  };

  const requiredMissing = summary?.required_missing ?? 0;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-ink-subtle">
          Item specifics checklist
        </span>

        <span className="flex items-center gap-2">
          <span className="text-[11px] text-ink-subtle">
            {`${summary?.filled ?? 0} / ${summary?.total ?? items.length}`}
          </span>

          {requiredMissing > 0
            ? (
              <span className="rounded-full bg-warning/13 px-2 py-[2px] text-[9.5px] font-semibold text-warning">
                {`${requiredMissing} required missing`}
              </span>
            )
            : null}
        </span>
      </span>

      <div className="flex flex-col gap-[7px]">
        {items.map((item) => {
          const current = values[item.code] ?? '';
          const missingRequired = item.is_required && !current;
          const dotClass = current
            ? 'bg-success'
            : missingRequired
              ? 'bg-danger'
              : 'bg-ink-subtle/40';

          return (
            <span
              key={item.code}
              className="flex items-center gap-2 rounded-lg border border-hairline bg-surface-2 px-3 py-2"
            >
              <span className="flex w-[110px] shrink-0 items-center gap-1.5">
                <span className="truncate text-[12.5px] text-ink-subtle">{item.name}</span>

                {item.is_required
                  ? (
                    <span
                      className={cn(
                        'rounded-full px-1.5 py-px text-[8.5px] font-semibold uppercase tracking-[0.4px]',
                        missingRequired ? 'bg-danger/13 text-danger' : 'bg-warning/13 text-warning',
                      )}
                    >
                      Required
                    </span>
                  )
                  : null}
              </span>

              {item.options?.length > 0
                ? (
                  <Select
                    value={current}
                    disabled={!canWrite}
                    className="min-w-0 flex-1"
                    selectClassName="h-8 rounded-md bg-transparent border-hairline text-[13px]"
                    onChange={(event) => setValue(item.code, event.target.value)}
                  >
                    <option value="">—</option>

                    {item.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )
                : (
                  <input
                    value={current}
                    disabled={!canWrite}
                    className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none disabled:opacity-70"
                    onChange={(event) => setValue(item.code, event.target.value)}
                  />
                )}

              <span
                aria-hidden
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  dotClass,
                  missingRequired && 'text-danger',
                )}
              />
            </span>
          );
        })}
      </div>

      {canWrite
        ? (
          <span className="mt-1 flex justify-end">
            <Button
              disabled={save.isPending}
              className={cn(
                'bg-brand-gradient h-auto rounded-lg px-[18px] py-2',
                'text-[13px] font-semibold text-white',
              )}
              onClick={onSave}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </span>
        )
        : null}

      <Toaster toasts={toasts} />
    </div>
  );
}

ItemSpecificsChecklistInner.displayName = 'ItemSpecificsChecklistInner';


/** Fillable item-specifics checklist for the CopyModal eBay tab (#273). Reads the
 * stored/resolved specifics, lets a writer fill required + optional attributes,
 * and PATCHes them back (blanks included, so clearing removes a value). Outer:
 * reads the query, gates loading/error, hides on an empty (no part-type) list, and
 * re-keys the inner by a value signature so edits re-seed on refetch without a
 * setState-in-effect. */
function ItemSpecificsChecklist({ sellerId, partId, canWrite, onSaved }) {
  const query = useQuery(`/inventory/parts/${sellerId}/${partId}/item-specifics/`);

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3.5 w-44" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return <WidgetError className="py-6" onRetry={() => query.refetch()} />;
  }

  const items = query.data?.items || [];
  if (!items.length) {
    return null;
  }

  // Remount the inner whenever the fetched values change so local edit state
  // re-seeds from props without a setState-in-effect.
  const signature = items.map((item) => `${item.code}=${item.value ?? ''}`).join('|');

  return (
    <ItemSpecificsChecklistInner
      items={items}
      key={signature}
      partId={partId}
      sellerId={sellerId}
      canWrite={canWrite}
      summary={query.data?.summary}
      onSaved={onSaved}
    />
  );
}

ItemSpecificsChecklist.displayName = 'ItemSpecificsChecklist';
export default ItemSpecificsChecklist;
